import { useState, useEffect } from 'react'
import { get } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'
import Pagination from '../../../../components/pagination/Pagination'
import './AdminLogs.scss'

interface AuditLog {
  _id: string
  adminId: string
  adminName: string
  action: string
  entity: string
  entityId: string
  role: string
  description: string
  targetAdminName?: string
  meta?: {
    previous?: any
    current?: any
  }
  createdAt: string
}

interface AuditLogsResponse {
  logs: AuditLog[]
  pagination: {
    total: number
    page: number
    limit: number
    pages: number
  }
}

function AdminLogs() {
  const { notify } = useGlobalContext()
  const [logs, setLogs] = useState<AuditLogsResponse>({
    logs: [],
    pagination: { total: 0, page: 1, limit: 10, pages: 0 },
  })
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: '',
    action: '',
    entity: '',
    adminId: '',
  })
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)

  function fetchLogs(page: number, pageSize: number) {
    setLoading(true)
    let url = `admin/audit-logs?page=${page}&limit=${pageSize}`

    // Add filters to URL if they exist
    if (filters.search) url += `&search=${filters.search}`
    if (filters.action) url += `&action=${filters.action}`
    if (filters.entity) url += `&entity=${filters.entity}`
    if (filters.adminId) url += `&adminId=${filters.adminId}`

    get(url)
      .then((result: any) => {
        if (result.status === 200) {
          setLogs(result.data)
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to fetch audit logs')
      })
  }

  useEffect(() => {
    fetchLogs(1, 10)
  }, [])

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSearch = () => {
    fetchLogs(1, 10)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getActionBadgeClass = (action: string) => {
    const actionMap: { [key: string]: string } = {
      UPDATE_ZONE_RATE: 'badge-warning',
      CHANGE_ADMIN_ROLE: 'badge-info',
      CREATE_ADMIN: 'badge-success',
      DELETE_ADMIN: 'badge-danger',
      UPDATE_ADMIN: 'badge-warning',
      LOGIN: 'badge-primary',
    }
    return actionMap[action] || 'badge-default'
  }

  return (
    <div className='home'>
      {selectedLog && (
        <div className='modal-overlay' onClick={() => setSelectedLog(null)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>Audit Log Details</h2>
              <Icon
                icon='mdi:close'
                onClick={() => setSelectedLog(null)}
                style={{ cursor: 'pointer', fontSize: '24px' }}
              />
            </div>
            <div className='modal-body'>
              <div className='log-detail'>
                <strong>Admin:</strong> {selectedLog.adminName} (
                {selectedLog.role})
              </div>
              <div className='log-detail'>
                <strong>Action:</strong> {selectedLog.action}
              </div>
              <div className='log-detail'>
                <strong>Entity:</strong> {selectedLog.entity}
              </div>
              <div className='log-detail'>
                <strong>Description:</strong> {selectedLog.description}
              </div>
              {selectedLog.targetAdminName && (
                <div className='log-detail'>
                  <strong>Target Admin:</strong> {selectedLog.targetAdminName}
                </div>
              )}
              <div className='log-detail'>
                <strong>Timestamp:</strong> {formatDate(selectedLog.createdAt)}
              </div>
              {selectedLog.meta && (
                <div className='log-detail'>
                  <strong>Changes:</strong>
                  <pre
                    style={{
                      background: '#f5f5f5',
                      padding: '10px',
                      borderRadius: '4px',
                      marginTop: '8px',
                    }}
                  >
                    {JSON.stringify(selectedLog.meta, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className='home_tag flex justify-between'>
        <h2>Admin Audit Logs</h2>
      </div>

      <div className='home_tables'>
        <div className='left'>
          <div
            className='flex justify-between items-center search-btns flex-wrap'
            style={{ gap: '10px', marginBottom: '20px' }}
          >
            <div className='search flex'>
              <input
                type='search'
                name='search'
                value={filters.search}
                placeholder='Search by admin name or description'
                onChange={handleFilterChange}
              />
              <button onClick={handleSearch}>Search</button>
            </div>

            <div className='flex' style={{ gap: '10px' }}>
              <select
                name='action'
                value={filters.action}
                onChange={handleFilterChange}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              >
                <option value=''>All Actions</option>
                <option value='UPDATE_ZONE_RATE'>Update Zone Rate</option>
                <option value='CHANGE_ADMIN_ROLE'>Change Admin Role</option>
                <option value='CREATE_ADMIN'>Create Admin</option>
                <option value='DELETE_ADMIN'>Delete Admin</option>
                <option value='UPDATE_ADMIN'>Update Admin</option>
                <option value='LOGIN'>Login</option>
              </select>

              <select
                name='entity'
                value={filters.entity}
                onChange={handleFilterChange}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              >
                <option value=''>All Entities</option>
                <option value='ZoneRate'>Zone Rate</option>
                <option value='Admin'>Admin</option>
                <option value='Booking'>Booking</option>
                <option value='Customer'>Customer</option>
              </select>
            </div>
          </div>

          {loading ? (
            <FormLoader position='center' />
          ) : (
            <>
              {logs.logs.length > 0 ? (
                <>
                  <div className='mobile-table'>
                    {logs.logs.map((log: AuditLog, i: number) => (
                      <div
                        className='card'
                        onClick={() => setSelectedLog(log)}
                        key={log._id}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className='flex justify-between row'>
                          <div className='count flex items-center justify-center'>
                            {(logs.pagination.page - 1) *
                              logs.pagination.limit +
                              i +
                              1}
                          </div>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Admin</h4>
                          <p>{log.adminName}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Action</h4>
                          <p>{log.action}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Entity</h4>
                          <p>{log.entity}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Description</h4>
                          <p>{log.description}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Timestamp</h4>
                          <p>{formatDate(log.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <table className='desktop-table'>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Admin</th>
                        <th>Role</th>
                        <th>Action</th>
                        <th>Entity</th>
                        <th>Description</th>
                        <th>Timestamp</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.logs.map((log: AuditLog, i: number) => (
                        <tr key={log._id}>
                          <td>
                            {(logs.pagination.page - 1) *
                              logs.pagination.limit +
                              i +
                              1}
                          </td>
                          <td className='count'>{log.adminName}</td>
                          <td className='capitalize'>{log.role}</td>
                          <td>
                            <span
                              className={`chip ${getActionBadgeClass(log.action)}`}
                            >
                              {log.action}
                            </span>
                          </td>
                          <td>{log.entity}</td>
                          <td
                            style={{
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {log.description}
                          </td>
                          <td>{formatDate(log.createdAt)}</td>
                          <td>
                            <span
                              className='view'
                              onClick={() => setSelectedLog(log)}
                            >
                              view
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <Pagination
                    currentPage={logs.pagination.page}
                    totalCount={logs.pagination.pages}
                    pageSize={logs.pagination.limit}
                    onPageChange={(page: number, pageSize: number) => {
                      fetchLogs(page, pageSize)
                    }}
                  />
                </>
              ) : (
                <div className='empty flex justify-center'>
                  <div className='empty-cover'>
                    <Icon icon='mdi:file-document-outline' />
                    <h1>No Audit Logs To Show</h1>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminLogs
